import React, { useState } from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";
import { SUPPORTED_CURRENCIES } from "../../../../utils/currencyUtils";
import "./GeneralTab.css";

const GeneralTab = ({ storeData, handleChange, logoPreview, handleLogoChange }) => {
    const [activeLanguage, setActiveLanguage] = useState('en');
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);

    // Handle multilingual content changes
    const handleLanguageChange = (field, value, language) => {
        // Ensure multilingualContent exists with proper structure
        const currentMultilingualContent = storeData.multilingualContent || {
            en: { name: "", description: "" },
            ar: { name: "", description: "" }
        };

        const updatedMultilingualContent = {
            ...currentMultilingualContent,
            [language]: {
                ...currentMultilingualContent[language],
                [field]: value
            }
        };

        handleChange({
            target: {
                name: 'multilingualContent',
                value: updatedMultilingualContent
            }
        });
    };

    // Get current values safely
    const getCurrentValue = (field, language) => {
        return storeData.multilingualContent?.[language]?.[field] || "";
    };
    return (
        <div className="general-settings-section">
            <h2>{t('storeInformation')}</h2>

            {/* Language Switcher */}
            <div className="language-switcher">
                <label className="switcher-label">Edit Content in:</label>
                <div className="language-tabs">
                    <button
                        type="button"
                        className={`language-tab ${activeLanguage === "en" ? "active" : ""}`}
                        onClick={() => setActiveLanguage("en")}
                    >
                        {t('english')}
                    </button>
                    <button
                        type="button"
                        className={`language-tab ${activeLanguage === "ar" ? "active" : ""}`}
                        onClick={() => setActiveLanguage("ar")}
                    >
                        {t('arabic')}
                    </button>
                </div>
            </div>
            <div className="settings-logo-section">
                <div className="logo-preview">
                    {logoPreview ? (
                        <img src={logoPreview} alt={t('storeLogo')} className="logo-img" />
                    ) : (
                        <div className="no-logo">
                            <span>🖼️ {t('noLogoUploaded')}</span>
                        </div>
                    )}
                </div>

                <div className="logo-upload">
                    <label htmlFor="logo" className="upload-label">{t('storeLogo')}</label>
                    <input
                        type="file"
                        id="logo"
                        name="logo"
                        onChange={handleLogoChange}
                        accept="image/*"
                        className="file-input"
                    />
                    <button
                        type="button"
                        className="admin-secondary-btn upload-logo-btn"
                        onClick={() => document.getElementById("logo").click()}
                    >
                        {t('uploadLogo')}
                    </button>

                    <p className="form-help">
                        Recommended size: <strong>200×200px</strong> <br />
                        <span className="note">✨ For best results, use a PNG with a transparent background</span>
                    </p>
                </div>
            </div>



            <div className="form-group">
                <label htmlFor="name">
                    {t('storeName')} ({activeLanguage === 'en' ? t('english') : t('arabic')}) *
                </label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={getCurrentValue('name', activeLanguage)}
                    onChange={(e) => handleLanguageChange('name', e.target.value, activeLanguage)}
                    placeholder={t('storeNamePlaceholder')}
                    dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                    required
                />
            </div>

            <div className="form-group">
                <label htmlFor="description">
                    {t('storeDescription')} ({activeLanguage === 'en' ? t('english') : t('arabic')})
                </label>
                <textarea
                    id="description"
                    name="description"
                    value={getCurrentValue('description', activeLanguage)}
                    onChange={(e) => handleLanguageChange('description', e.target.value, activeLanguage)}
                    placeholder={t('storeDescriptionPlaceholder')}
                    dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
                    rows="3"
                />
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="email">{t('emailAddress')}</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={storeData.email}
                        onChange={handleChange}
                        placeholder={t('emailPlaceholder')}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="phone">{t('phoneNumber')}</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={storeData.phone}
                        onChange={handleChange}
                        placeholder={t('phonePlaceholder')}
                    />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="address">{t('address')}</label>
                <textarea
                    id="address"
                    name="address"
                    value={storeData.address}
                    onChange={handleChange}
                    placeholder={t('addressPlaceholder')}
                    rows="2"
                />
            </div>

            <div className="form-group">
                <label htmlFor="domain">{t('customDomain') || 'Custom Domain'}</label>
                <input
                    type="url"
                    id="domain"
                    name="domain"
                    value={storeData.domain || ''}
                    onChange={handleChange}
                    placeholder="https://yourdomain.com or subdomain.raffak.store"
                />
                <p className="form-help">
                    <span className="note">🌐 Enter your custom domain (e.g., https://yourstore.com)</span><br />
                    <span className="note">This will be used for payment redirects and email links</span>
                </p>
            </div>

            <div className="form-group">
                <label htmlFor="currency" className="form-label">
                    {t('currency')}
                </label>
                <select
                    id="currency"
                    name="currency"
                    className="currency-select"
                    value={storeData.currency}
                    onChange={handleChange}
                >
                    {Object.values(SUPPORTED_CURRENCIES).map(currency => (
                        <option key={currency.code} value={currency.code}>
                            {currency.en.name} ({currency.code}) - {currency.symbol}
                        </option>
                    ))}
                </select>
            </div>

            <h2 style={{ marginTop: '2rem' }}>{t('brandColors')}</h2>
            <p className="section-help">{t('brandColorsDescription')}</p>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="brandColors.primary">
                        {t('primaryColor')}
                    </label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            id="brandColors.primary"
                            name="brandColors.primary"
                            value={storeData.brandColors?.primary || '#182957'}
                            onChange={handleChange}
                            className="color-picker"
                        />
                        <input
                            type="text"
                            value={storeData.brandColors?.primary || '#182957'}
                            onChange={(e) => handleChange({ target: { name: 'brandColors.primary', value: e.target.value } })}
                            placeholder="#182957"
                            className="color-text-input"
                        />
                    </div>
                    <span className="color-help">{t('primaryColorHelp')}</span>
                </div>

                <div className="form-group">
                    <label htmlFor="brandColors.hover">
                        {t('hoverColor')}
                    </label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            id="brandColors.hover"
                            name="brandColors.hover"
                            value={storeData.brandColors?.hover || '#233a79'}
                            onChange={handleChange}
                            className="color-picker"
                        />
                        <input
                            type="text"
                            value={storeData.brandColors?.hover || '#233a79'}
                            onChange={(e) => handleChange({ target: { name: 'brandColors.hover', value: e.target.value } })}
                            placeholder="#233a79"
                            className="color-text-input"
                        />
                    </div>
                    <span className="color-help">{t('hoverColorHelp')}</span>
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="brandColors.text">
                        {t('textColor')}
                    </label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            id="brandColors.text"
                            name="brandColors.text"
                            value={storeData.brandColors?.text || '#181317'}
                            onChange={handleChange}
                            className="color-picker"
                        />
                        <input
                            type="text"
                            value={storeData.brandColors?.text || '#181317'}
                            onChange={(e) => handleChange({ target: { name: 'brandColors.text', value: e.target.value } })}
                            placeholder="#181317"
                            className="color-text-input"
                        />
                    </div>
                    <span className="color-help">{t('textColorHelp')}</span>
                </div>

                <div className="form-group">
                    <label htmlFor="brandColors.light">
                        {t('lightColor')}
                    </label>
                    <div className="color-input-group">
                        <input
                            type="color"
                            id="brandColors.light"
                            name="brandColors.light"
                            value={storeData.brandColors?.light || '#e7edf7'}
                            onChange={handleChange}
                            className="color-picker"
                        />
                        <input
                            type="text"
                            value={storeData.brandColors?.light || '#e7edf7'}
                            onChange={(e) => handleChange({ target: { name: 'brandColors.light', value: e.target.value } })}
                            placeholder="#e7edf7"
                            className="color-text-input"
                        />
                    </div>
                    <span className="color-help">{t('lightColorHelp')}</span>
                </div>
            </div>


        </div>
    );
};

export default GeneralTab;